<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateContract',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    rentalId: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mapDefaultContract = (...args) => {
    const data = args[0];
    return _.extend(data, {
      counterparty:
        data.counterparty && data.counterparty.id ? data.counterparty : null,
      company: data.company && data.company.id ? data.company : null,
      rental: data.rental && data.rental.id ? data.rental : null,
    });
  };

  const contract_type = ref('rental');
  const contract = ref(null);
  const contractTypes = ref(null);
  const formName = ref('contract');
  const formId = ref(null);
  const labelAlign = ref('left');
  const currentTab = ref(0);
  const categories = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    contract_type: contract_type.value,
    contract: contract.value,
    contractTypes: contractTypes.value,
    formName: formName.value,
    formId: formId.value,
    labelAlign: labelAlign.value,
    currentTab: currentTab.value,
    categories: categories.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_contract = async () => {
    return $fn.value
      .fetch('read_contract', {
        parameters: {
          id: 'defaults',
          type: $data.value.contract_type,
          rental: props.rentalId,
        },
      })
      .then((data) => mapDefaultContract(data));
  };
  const $$load_contractTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('ContractTypes');
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_categories = async () => {
    return $fn.value.fetch('list_contract_category');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      {
        name: 'contract_type',
        cache: { type: 'local', key: 'CreateContract.contractType' },
      },
      { name: 'contract' },
      { name: 'contractTypes' },
      { name: 'formName', dependsOn: ['contract'] },
      { name: 'formId' },
      { name: 'labelAlign' },
      { name: 'currentTab' },
      { name: 'categories' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {
      contract: $$load_contract,
      contractTypes: $$load_contractTypes,
      formId: $$load_formId,
      categories: $$load_categories,
    },
    ux: {},
    dataRefs: {
      contract_type,
      contract,
      contractTypes,
      formName,
      formId,
      labelAlign,
      currentTab,
      categories,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const companyTranslation = computed(() => {
    if (loaded.value) {
      return ['rental', 'rental_agreement', 'parking'].indexOf(
        contract.value.contract_type
      ) > -1
        ? $fn.value.pgettext('Contract', 'Lessor')
        : $fn.value.pgettext('Contract', 'Company');
    }
    return null;
  });
  const counterpartyTranslation = computed(() => {
    if (loaded.value) {
      return ['rental', 'rental_agreement', 'parking'].indexOf(
        contract.value.contract_type
      ) > -1
        ? $fn.value.pgettext('Contract', 'Tenant')
        : $fn.value.pgettext('Contract', 'Counterparty');
    }
    return null;
  });
  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Create Contract') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.rentalId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('rentalId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_0gfvljpa9_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_7789867021953041_setData_hxtxrjfps_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'contract',
      value: mapDefaultContract($event.data.response.data),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_7789867021953041_request_7s4wcly0v_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'read_contract',
      parameters: {
        id: 'defaults',
        type: $event.data.value,
      },
    };
  };
  const act_3322283983417540_setData_y6p89lqnn_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'currentTab',
      value: currentTab.value - 1,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_8328328512915885_setData_ra1w7nvho_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'currentTab',
      value: currentTab.value + 1,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2132444698984839_reloadSlickgrid_9yhfcfco8_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'contracts',
    };
  };
  const act_2132444698984839_closeComponent_o8kr4x95j_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const act_2132444698984839_navigate_28dj1ehp9_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.contract',
        params: {
          id: $event.data.id,
        },
      },
    };
  };
  const act_2132444698984839_crud_0efylv8zk_getActionArgs = async ($event) => {
    return {
      objectType: 'contract',
      op: 'upsert',
      data: contract.value,
    };
  };

  const act_7315092382398562_closeModal_0gfvljpa9 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_0gfvljpa9_getActionArgs,
    });
  const inst_evh_7315092382398562_close_w2rn3vkw7 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_0gfvljpa9,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_w2rn3vkw7],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_nxua129p1 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_x48cq96ti =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_nxua129p1,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_x48cq96ti],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_3qqjm4lfp =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_vxtxo2b0m =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_3qqjm4lfp,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_vxtxo2b0m],
    event: 'close',
  });
  const act_7789867021953041_setData_hxtxrjfps = new actionTypes.SetDataAction({
    actionArgs: act_7789867021953041_setData_hxtxrjfps_getActionArgs,
  });
  const inst_evh_7789867021953041_success_w5319vs2a =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_setData_hxtxrjfps,
      event: 'success',
      displayName: 'setData',
    });
  const act_7789867021953041_request_7s4wcly0v = new actionTypes.RequestAction({
    actionArgs: act_7789867021953041_request_7s4wcly0v_getActionArgs,
    events: [inst_evh_7789867021953041_success_w5319vs2a],
  });
  const inst_evh_7789867021953041_optionSelected_hd9xvvt2e =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_request_7s4wcly0v,
      event: 'optionSelected',
      displayName: 'request',
    });
  const inst_evh_7789867021953041_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7789867021953041_optionSelected_hd9xvvt2e],
      event: 'optionSelected',
    });
  const act_3322283983417540_setData_y6p89lqnn = new actionTypes.SetDataAction({
    actionArgs: act_3322283983417540_setData_y6p89lqnn_getActionArgs,
  });
  const inst_evh_3322283983417540_clickPreventStop_3l51vbp5y =
    new actionTypes.EventHandlerImpl({
      action: act_3322283983417540_setData_y6p89lqnn,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_3322283983417540_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3322283983417540_clickPreventStop_3l51vbp5y],
      event: 'click.prevent.stop',
    });
  const act_8328328512915885_setData_ra1w7nvho = new actionTypes.SetDataAction({
    actionArgs: act_8328328512915885_setData_ra1w7nvho_getActionArgs,
  });
  const inst_evh_8328328512915885_clickPreventStop_n8hq6qa0c =
    new actionTypes.EventHandlerImpl({
      action: act_8328328512915885_setData_ra1w7nvho,
      event: 'click.prevent.stop',
      displayName: 'setData',
    });
  const inst_evh_8328328512915885_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8328328512915885_clickPreventStop_n8hq6qa0c],
      event: 'click.prevent.stop',
    });
  const act_2132444698984839_reloadSlickgrid_9yhfcfco8 =
    new actionTypes.ReloadSlickgridAction({
      actionArgs: act_2132444698984839_reloadSlickgrid_9yhfcfco8_getActionArgs,
    });
  const inst_evh_2132444698984839_success_yjqfnugrr =
    new actionTypes.EventHandlerImpl({
      action: act_2132444698984839_reloadSlickgrid_9yhfcfco8,
      event: 'success',
      displayName: 'reloadSlickgrid',
    });
  const act_2132444698984839_closeComponent_o8kr4x95j =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2132444698984839_closeComponent_o8kr4x95j_getActionArgs,
    });
  const inst_evh_2132444698984839_success_qw7a9b2j3 =
    new actionTypes.EventHandlerImpl({
      action: act_2132444698984839_closeComponent_o8kr4x95j,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_2132444698984839_navigate_28dj1ehp9 =
    new actionTypes.NavigateAction({
      actionArgs: act_2132444698984839_navigate_28dj1ehp9_getActionArgs,
    });
  const inst_evh_2132444698984839_success_kpus8jh12 =
    new actionTypes.EventHandlerImpl({
      action: act_2132444698984839_navigate_28dj1ehp9,
      event: 'success',
      displayName: 'navigate',
    });
  const act_2132444698984839_crud_0efylv8zk = new actionTypes.CRUDAction({
    actionArgs: act_2132444698984839_crud_0efylv8zk_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_2132444698984839_success_yjqfnugrr,
      inst_evh_2132444698984839_success_qw7a9b2j3,
      inst_evh_2132444698984839_success_kpus8jh12,
    ],
  });
  const inst_evh_2132444698984839_clickPreventStop_qa85hzkum =
    new actionTypes.EventHandlerImpl({
      action: act_2132444698984839_crud_0efylv8zk,
      event: 'click.prevent.stop',
      displayName: 'save',
    });
  const inst_evh_2132444698984839_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2132444698984839_clickPreventStop_qa85hzkum],
      event: 'click.prevent.stop',
    });
  const act_2076709591253261_closeModal_vckyq64us =
    new actionTypes.CloseModalAction({});
  const inst_evh_2076709591253261_clickPreventStop_x6ib51usg =
    new actionTypes.EventHandlerImpl({
      action: act_2076709591253261_closeModal_vckyq64us,
      event: 'click.prevent.stop',
      displayName: 'closeModal',
    });
  const inst_evh_2076709591253261_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2076709591253261_clickPreventStop_x6ib51usg],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_w2rn3vkw7,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_x48cq96ti,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_vxtxo2b0m,
    inst_evh_8564662037462133_close,
    inst_evh_7789867021953041_success_w5319vs2a,
    inst_evh_7789867021953041_optionSelected_hd9xvvt2e,
    inst_evh_7789867021953041_optionSelected,
    inst_evh_3322283983417540_clickPreventStop_3l51vbp5y,
    inst_evh_3322283983417540_clickPreventStop,
    inst_evh_8328328512915885_clickPreventStop_n8hq6qa0c,
    inst_evh_8328328512915885_clickPreventStop,
    inst_evh_2132444698984839_success_yjqfnugrr,
    inst_evh_2132444698984839_success_qw7a9b2j3,
    inst_evh_2132444698984839_success_kpus8jh12,
    inst_evh_2132444698984839_clickPreventStop_qa85hzkum,
    save: inst_evh_2132444698984839_clickPreventStop_qa85hzkum,
    inst_evh_2132444698984839_clickPreventStop,
    inst_evh_2076709591253261_clickPreventStop_x6ib51usg,
    inst_evh_2076709591253261_clickPreventStop,
  };

  const evh_7315092382398562_close_w2rn3vkw7 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_w2rn3vkw7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_x48cq96ti = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_x48cq96ti.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_vxtxo2b0m = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_vxtxo2b0m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_success_w5319vs2a = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_success_w5319vs2a.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected_hd9xvvt2e = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7789867021953041_optionSelected_hd9xvvt2e.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_optionSelected = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_clickPreventStop_3l51vbp5y = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3322283983417540_clickPreventStop_3l51vbp5y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3322283983417540_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3322283983417540_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8328328512915885_clickPreventStop_n8hq6qa0c = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8328328512915885_clickPreventStop_n8hq6qa0c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8328328512915885_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8328328512915885_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2132444698984839_success_yjqfnugrr = async (event, scope) => {
    return await actions.inst_evh_2132444698984839_success_yjqfnugrr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2132444698984839_success_qw7a9b2j3 = async (event, scope) => {
    return await actions.inst_evh_2132444698984839_success_qw7a9b2j3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2132444698984839_success_kpus8jh12 = async (event, scope) => {
    return await actions.inst_evh_2132444698984839_success_kpus8jh12.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2132444698984839_clickPreventStop_qa85hzkum = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2132444698984839_clickPreventStop_qa85hzkum.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2132444698984839_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2132444698984839_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2076709591253261_clickPreventStop_x6ib51usg = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2076709591253261_clickPreventStop_x6ib51usg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2076709591253261_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2076709591253261_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateContract"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <div class="px-3">
        <ItpFormGroup
          name="contract_type"
          :label="gettext('Contract type')"
          row
          :label-cols-md="3"
          :content-cols-md="4"
          required
        >
          <ItpFormSelect2
            name="contract_type"
            v-model="contract_type"
            :value="contract_type"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (contract_type =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="contractTypes"
            valueField="value"
            textField="text"
            required
            @optionSelected="evh_7789867021953041_optionSelected($event, {})"
          >
          </ItpFormSelect2>
        </ItpFormGroup>
      </div>
      <hr />
      <ItpForm :id="formId" :name="formName" class="px-3">
        <BTabs v-model:index="currentTab" vertical pills contentClass="col">
          <BTab :title="pgettext('Contract', 'General')" class="px-3">
            <FieldsetContractNumbersData
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractNumbersData>
            <hr />
            <FieldsetContractValidityData
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractValidityData>
          </BTab>
          <BTab :title="companyTranslation" class="px-3">
            <FieldsetContractCompanyData
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractCompanyData>
          </BTab>
          <BTab :title="counterpartyTranslation" class="px-3">
            <FieldsetContractClientData
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractClientData>
          </BTab>
          <BTab :title="pgettext('Contract', 'Rental')" class="px-3">
            <FieldsetContractRentalData
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractRentalData>
          </BTab>
          <BTab :title="pgettext('Contract', 'Financial')" class="px-3">
            <FieldsetContractInvoicing
              :contract="contract"
              :labelAlign="labelAlign"
            >
            </FieldsetContractInvoicing>
          </BTab>
        </BTabs>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpBox class="me-auto">
          <ItpButton
            :text="gettext('Previous')"
            :disabled="currentTab == 0"
            @click.prevent.stop="
              evh_3322283983417540_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
        <ItpBox :gap="2">
          <ItpButton
            :text="gettext('Next')"
            :disabled="currentTab > 3"
            @click.prevent.stop="
              evh_8328328512915885_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            type="submit"
            :form="formId"
            variant="primary"
            icon="fa-save"
            :text="gettext('Create contract')"
            :spinning="actions.save.isRunning"
            :disabled="!forms[formName] || !forms[formName].submittable"
            @click.prevent.stop="
              evh_2132444698984839_clickPreventStop($event, {})
            "
          >
          </ItpButton>
          <ItpButton
            variant="default"
            :text="gettext('Close')"
            :disabled="actions.save.isRunning"
            @click.prevent.stop="
              evh_2076709591253261_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </UcFooter>
    </template>
  </ItpPage>
</template>
